import { Engine } from '@babylonjs/core';
import { GameApp } from './app/GameApp';
import { startGame } from './app/startGame';

// Global reference for debugging
declare global {
  interface Window {
    game: GameApp;
  }
}

function init(): void {
  const canvas = document.getElementById('game-canvas') as HTMLCanvasElement;
  const gameFrame = document.querySelector<HTMLElement>('.game-frame')!;
  const gameUnavailable = document.getElementById('game-unavailable')!;
  const permissionOverlay = document.getElementById('permission-overlay')!;
  const permissionBtn = document.getElementById('permission-btn')!;
  const winOverlay = document.getElementById('win-overlay')!;
  const winTime = document.getElementById('win-time')!;
  const winMedal = document.getElementById('win-medal')!;
  const restartBtn = document.getElementById('restart-btn')!;
  const fellOverlay = document.getElementById('fell-overlay')!;
  const startRunLinks = document.querySelectorAll<HTMLAnchorElement>('[data-start-run]');

  if (!canvas) {
    console.error('Canvas element not found');
    return;
  }

  // Never leave a full-page permission prompt covering a game that did not
  // initialize. The ready callback will reveal it only when motion permission
  // is actually needed.
  permissionOverlay.classList.add('hidden');

  const showUnavailable = () => {
    gameFrame.classList.add('is-unavailable');
    gameUnavailable.hidden = false;
  };

  const game = startGame({
    webglSupported: Engine.IsSupported,
    createGame: () => new GameApp(canvas),
    showUnavailable,
  });

  if (!game) return;

  window.game = game; // For debugging

  // Start game loop
  game.start();

  // A public "play" action should begin a fresh timed run, not merely scroll
  // to a table whose clock started while the visitor was reading the intro.
  startRunLinks.forEach((link) => {
    link.addEventListener('click', () => {
      winOverlay.classList.add('hidden');
      fellOverlay.classList.add('hidden');
      game.resetRun();
      requestAnimationFrame(() => canvas.focus({ preventScroll: true }));
    });
  });

  // Unlock audio on first user gesture (required for autoplay policy)
  let audioUnlocked = false;
  const unlockAudio = () => {
    if (audioUnlocked) return;
    audioUnlocked = true;
    void game.audio.resume();
  };
  window.addEventListener('pointerdown', unlockAudio, { once: true });
  window.addEventListener('keydown', unlockAudio, { once: true });

  game.onReady = () => {
    const needsPermission = game.mode === 'permission-gate';
    permissionOverlay.classList.toggle('hidden', !needsPermission);
    console.log('Game ready, mode:', game.mode, 'needsPermission:', needsPermission);
  };

  // Handle win
  game.onWin = (time: number, medal: string) => {
    winTime.textContent = `Time: ${time.toFixed(2)}s`;

    // Medal emoji
    const medalEmoji = medal === 'gold' ? '🥇' : medal === 'silver' ? '🥈' : medal === 'bronze' ? '🥉' : '';
    winMedal.textContent = medalEmoji;

    winOverlay.classList.remove('hidden');
  };

  // Handle restart from win screen
  restartBtn.addEventListener('click', () => {
    winOverlay.classList.add('hidden');
    game.resetRun();
  });

  // Handle fell (ball in hole)
  game.onFell = () => {
    fellOverlay.classList.remove('hidden');
  };

  // Tap anywhere on fell overlay to restart
  fellOverlay.addEventListener('pointerdown', () => {
    fellOverlay.classList.add('hidden');
    game.resetRun();
  });

  // Handle permission button click
  permissionBtn.addEventListener('click', async () => {
    permissionBtn.textContent = 'Enabling...';
    await game.requestMotionPermission();
    permissionOverlay.classList.add('hidden');
  });

  // Also allow tap anywhere on overlay
  permissionOverlay.addEventListener('click', async (e) => {
    if (e.target === permissionOverlay) {
      permissionBtn.textContent = 'Enabling...';
      await game.requestMotionPermission();
      permissionOverlay.classList.add('hidden');
    }
  });

  // Handle visibility change (pause when hidden)
  document.addEventListener('visibilitychange', () => {
    if (document.hidden) {
      // Could pause here
    }
  });

  // Clean up on page unload
  window.addEventListener('beforeunload', () => {
    game.dispose();
  });

  // Hot module replacement support
  if (import.meta.hot) {
    import.meta.hot.dispose(() => {
      game.dispose();
    });
  }

  console.log('Tiltrun initialized');
}

// Wait for DOM
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init);
} else {
  init();
}
